import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import api from '../utils/api';
import { fbTrack } from '../utils/pixel';

// Routes where the sales widget should NOT appear
const HIDDEN_ROUTES = ['/dashboard', '/admin', '/checkout', '/auth', '/success'];

const STORAGE_KEY_SESSION = 'ayronn_sales_session_id';
const STORAGE_KEY_VISITOR = 'ayronn_visitor_id';
const STORAGE_KEY_OPENED_ONCE = 'ayronn_sales_opened_once';

const GREETING = "Hey! 👋 I'm AYRONN AI — I'll help you pick the perfect challenge. New to prop trading or already done some?";

// Make assistant replies feel alive: convert /pricing, /auth mentions to clickable links
const renderMessage = (text) => {
  if (!text) return null;
  const parts = text.split(/(\/pricing|\/auth)/g);
  return parts.map((p, i) => {
    if (p === '/pricing') return <a key={i} href="/pricing" className="underline text-brand-primary font-semibold" onClick={() => fbTrack('Lead', { content_name: 'sales_bot_cta_pricing' })}>/pricing</a>;
    if (p === '/auth') return <a key={i} href="/auth" className="underline text-brand-primary font-semibold" onClick={() => fbTrack('Lead', { content_name: 'sales_bot_cta_signup' })}>/auth</a>;
    return <span key={i}>{p}</span>;
  });
};

const getOrCreate = (key) => {
  let v = localStorage.getItem(key);
  if (!v) {
    v = `${key.replace('ayronn_', '')}-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
    localStorage.setItem(key, v);
  }
  return v;
};

const SalesChatWidget = () => {
  const { pathname } = useLocation();
  const hidden = HIDDEN_ROUTES.some(r => pathname.startsWith(r));
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(localStorage.getItem(STORAGE_KEY_SESSION) || '');

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  if (hidden) return null;

  const handleOpen = () => {
    setOpen(true);
    if (!localStorage.getItem(STORAGE_KEY_OPENED_ONCE)) {
      localStorage.setItem(STORAGE_KEY_OPENED_ONCE, '1');
      fbTrack('Lead', { content_name: 'sales_bot_opened' });
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    setMessages(m => [...m, { role: 'user', content: text }]);
    if (!textOverride) setInput('');
    try {
      const { data } = await api.post('/sales/chat', {
        message: text,
        session_id: sessionIdRef.current || null,
        visitor_id: getOrCreate(STORAGE_KEY_VISITOR),
      });
      if (data.session_id && !sessionIdRef.current) {
        sessionIdRef.current = data.session_id;
        localStorage.setItem(STORAGE_KEY_SESSION, data.session_id);
      }
      setMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      const errMsg = e?.response?.data?.detail || 'Connection issue. Please try again or visit /pricing.';
      setMessages(m => [...m, { role: 'assistant', content: errMsg, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const quickPrompts = [
    "Which plan is best for beginners?",
    "How do daily payouts work?",
    "Show me the cheapest challenge",
    "Is this legit?",
  ];

  return (
    <>
      {/* Floating Bubble */}
      {!open && (
        <button
          onClick={handleOpen}
          className={`fixed bottom-24 md:bottom-5 right-4 md:right-5 z-[60] group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-brand-primary to-[#00B4D8] text-black font-bold shadow-[0_0_30px_rgba(0,212,255,0.45)] hover:shadow-[0_0_40px_rgba(0,212,255,0.65)] transition-all duration-300`}
          data-testid="sales-chat-bubble"
          aria-label="Open sales assistant chat"
        >
          <div className="relative">
            <MessageCircle className="w-5 h-5" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-brand-positive rounded-full ring-2 ring-black/20 animate-pulse" />
          </div>
          <span className="text-sm">Chat with AYRONN AI</span>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-24 md:bottom-5 right-4 md:right-5 z-[60] w-[360px] max-w-[calc(100vw-32px)] h-[540px] max-h-[calc(100vh-140px)] md:max-h-[calc(100vh-40px)] flex flex-col bg-[#0A0F1C]/95 backdrop-blur-xl border border-brand-primary/30 rounded-2xl shadow-[0_0_40px_rgba(0,212,255,0.25)] overflow-hidden"
          data-testid="sales-chat-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between bg-gradient-to-r from-brand-primary/15 to-transparent border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-brand-primary to-[#00B4D8] flex items-center justify-center shadow-lg">
                <Sparkles className="w-4.5 h-4.5 text-black" />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-brand-positive rounded-full ring-2 ring-[#0A0F1C]" />
              </div>
              <div>
                <div className="text-white text-sm font-bold leading-tight">AYRONN AI</div>
                <div className="text-[10px] text-brand-positive flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-brand-positive rounded-full animate-pulse" />
                  Online now · typically replies instantly
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 transition-colors" data-testid="sales-chat-close" aria-label="Close chat">
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sales-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap break-words ${m.role === 'user' ? 'bg-brand-primary text-black font-medium rounded-br-sm' : m.error ? 'bg-red-500/10 border border-red-500/30 text-red-300 rounded-bl-sm' : 'bg-white/5 border border-white/10 text-zinc-100 rounded-bl-sm'}`}>
                  {m.role === 'assistant' && !m.error ? renderMessage(m.content) : m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl rounded-bl-sm flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                  <span className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                </div>
              </div>
            )}
          </div>

          {/* Quick Prompts (only when greeting is the only msg) */}
          {messages.length <= 1 && !sending && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {quickPrompts.map((q, i) => (
                <button
                  key={i}
                  onClick={() => send(q)}
                  className="text-[11px] px-2.5 py-1.5 bg-white/5 border border-white/10 text-zinc-300 rounded-full hover:bg-brand-primary/15 hover:border-brand-primary/40 hover:text-white transition-all"
                  data-testid={`sales-quick-${i}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-white/10 flex gap-2 bg-[#050505]/60">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Type your message..."
              disabled={sending}
              className="flex-1 px-3.5 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/40 outline-none disabled:opacity-50"
              data-testid="sales-chat-input"
              maxLength={1500}
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="px-3 bg-brand-primary text-black rounded-lg hover:glow-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
              data-testid="sales-chat-send"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          {/* Footer */}
          <div className="text-[10px] text-zinc-600 text-center pb-1.5 bg-[#050505]/60">
            Powered by Claude Sonnet 4.5 · <a href="/pricing" className="text-brand-primary/70 hover:text-brand-primary">Browse plans</a>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesChatWidget;
